import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Capabilities } from '@/auth-capabilities';
import { useAuth } from '@/context/appContext';
import { useFeatures } from '@/context/FeatureContext';
import { useRegionalApis } from '@/context/RegionScope';
import { logTrackedError } from '@/errorTracking';
import * as Sentry from '@/sentry';
import { useSocketEvent, useSocketIOClient, useSocketOnConnect } from '@/socketio/SocketIOService';
import { debounce } from '@/utils/debounce';
import { queueItemPath } from '@/utils/queueItemRoute';
import { telemetrySend } from '@/utils/telemetry';

import { useBrowserNotifications } from './BrowserNotificationService';
import { useMealQueueService, useQueueLastRefetchedAt } from './MealQueueService';
import { useCombinedGetNextQueue } from './useCombinedGetNextQueue';
import { CombinedQueueProvider } from './useCombinedQueue';

let normalFavicon: string = '';

/**
 * Internal hook responsible for blinking the favicon while `alerting` is true.
 */
const useNewQueueFaviconAlert = (opts: { alerting: boolean }) => {
  const flags = useFeatures();

  const alertIcon = '/favicon-alert.png';
  useEffect(() => {
    const linkElement = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (!linkElement) {
      return;
    }

    if (!normalFavicon) {
      normalFavicon = linkElement.href;
    }

    if (!opts.alerting || !flags.notification_favicon_blink) {
      linkElement.href = normalFavicon;
      return;
    }

    const interval = setInterval(() => {
      linkElement.href = linkElement.href == normalFavicon ? alertIcon : normalFavicon;
    }, 350);

    return () => clearInterval(interval);
  }, [opts.alerting, flags]);
};

const getNextQueueClickedEmitter = new EventTarget();

/**
 * Provides a `getNextQueue` function that will navigate to the next queue item
 * which needs labeling.
 */
export const useGetNextQueue = () => {
  const navigate = useNavigate();
  const { authInfo } = useAuth();
  const { region, dataReviewApi } = useRegionalApis();
  const [disableUntilRefetch, setDisableUntilRefetch] = useState<boolean>(false);
  const { ownedQueues, refetch } = useMealQueueService();
  const lastRefetchedAt = useQueueLastRefetchedAt();

  useEffect(() => {
    setDisableUntilRefetch(false);
  }, [lastRefetchedAt]);

  const getNextQueue = React.useCallback(
    async (opts: { source: string; medium?: string }) => {
      getNextQueueClickedEmitter.dispatchEvent(new Event('click'));
      const params = { utm_source: opts.source, utm_medium: opts.medium };
      const firstOwnedQueue = ownedQueues[0];
      if (firstOwnedQueue) {
        navigate(queueItemPath(firstOwnedQueue.id, region, params), {
          state: {
            queueItem: firstOwnedQueue,
            region,
          },
        });
        return;
      }

      if (!authInfo) {
        return;
      }

      try {
        const nextItemRes = await dataReviewApi.appApiDataReviewerGetDataReviewerNextQueue({
          data_reviewer_id: authInfo.reviewer_id,
        });
        const nextItem = nextItemRes.status == 204 ? null : nextItemRes.data;
        if (!nextItem) {
          console.log('Get Next: no next item');
          setDisableUntilRefetch(true);
          refetch('get-next');
          return;
        }
        navigate(queueItemPath(nextItem.id, region, params), {
          state: {
            queueItem: nextItem,
            region,
          },
        });
      } catch (e) {
        logTrackedError({
          sourceName: 'useGetNextQueue.getNextQueue',
          origin: e as any,
          stackError: new Error(),
          context: {},
          userMessage: `Unable to get next queue. Please try again.`,
        });
      }
    },
    [navigate, authInfo, ownedQueues, refetch, region, dataReviewApi],
  );

  return {
    getNextQueue,
    disableUntilRefetch,
  };
};

// Used by the `MealQueueNotificationServiceInner`
let lastNotificationTimestamp: string | null = null;
const getNextClickedStats = {
  count: 0,
  firstTimestamp: 0,
};

export type NotificationEngineQueueState = 'unassigned' | 'assigned' | 'complete';

export type NotificationEngineQueueEvent = { queueId: number; newState: NotificationEngineQueueState };

/**
 * Whether a `queues:refetch` batch should refetch the available queues.
 *
 * A queue we already list refetches when its state changed. A queue we do not
 * list refetches only when it became `unassigned` (new work for us). Org-wide
 * `assigned`/`complete` events for unknown queues are skipped: treating them as
 * changes made every page refetch on every claim. The trade-off is that a claim
 * made from another tab is picked up by the 60s interval refetch instead.
 */
export const shouldRefetchForQueueEvents = (
  activeQueues: Array<{ id: number; is_processed?: boolean | null; first_reviewer_user_id?: number | null }>,
  events: NotificationEngineQueueEvent[],
): boolean => {
  const activeQueueStates = Object.fromEntries(
    activeQueues.map(q => [q.id, q.is_processed ? 'complete' : q.first_reviewer_user_id ? 'assigned' : 'unassigned']),
  ) as Record<number, NotificationEngineQueueState>;
  return events.some(e => {
    const knownState = activeQueueStates[e.queueId];
    if (knownState !== undefined) {
      return knownState !== e.newState;
    }
    return e.newState === 'unassigned';
  });
};

// Singleton component that provides notifications when the user has work to do. Used
// only by the `MealQueueNotificationService`, which should only be mounted once in the
// top level of the app.
const MealQueueNotificationServiceInner = () => {
  const { authInfo } = useAuth();
  const { notify } = useBrowserNotifications();
  const { getNextQueue } = useCombinedGetNextQueue();
  const mealQueueService = useMealQueueService();
  const [currentAlertId, setCurrentAlertId] = useState<number | null>(null);
  const flags = useFeatures();

  const io = useSocketIOClient();
  useSocketOnConnect(
    io,
    useCallback(socket => {
      socket.emit('queues:join');
    }, []),
  );

  const debouncedQueueRefetch = React.useMemo(
    () =>
      debounce(
        () => {
          mealQueueService.refetch('notification-engine:queues:refetch');
        },
        3000,
        { maxWait: 10000 },
      ),
    [mealQueueService.refetch],
  );

  useEffect(() => {
    return () => {
      debouncedQueueRefetch.cancel();
    };
  }, [debouncedQueueRefetch]);

  useSocketEvent(
    io,
    'queues:refetch',
    useCallback(
      (events: NotificationEngineQueueEvent[]) => {
        const shouldRefetch = shouldRefetchForQueueEvents(mealQueueService.allActiveQueues, events);
        if (!shouldRefetch) {
          return;
        }
        debouncedQueueRefetch();
      },
      [mealQueueService.allActiveQueues, debouncedQueueRefetch],
    ),
  );

  // When "get next" is clicked, track the number of counts, and the timestamp
  // of the first count.
  // Note: the "alerting" hook, below, will reset this state when alerting starts.
  useEffect(() => {
    const handleGetNextClicked = () => {
      getNextClickedStats.count += 1;
      if (!getNextClickedStats.firstTimestamp) {
        getNextClickedStats.firstTimestamp = Date.now();
      }
    };
    getNextQueueClickedEmitter.addEventListener('click', handleGetNextClicked);
    return () => getNextQueueClickedEmitter.removeEventListener('click', handleGetNextClicked);
  }, []);

  // Determine whether the application should be in an "alerting" state (ie,
  // there is work for the user to do)
  const isAlerting =
    !mealQueueService.currentlyLabellingOwnQueue
    && (mealQueueService.ownedCount > 0 || mealQueueService.availableCount > 0)
    && (!flags.notification_UHP_only || mealQueueService.allActiveQueues.some(q => q.is_ultra_priority_patient));

  // Blink the favicon when alerting
  useNewQueueFaviconAlert({ alerting: isAlerting });

  // Track alerting start/finish (telemetry payload kept commented — unused).
  const isLoadingQueues = mealQueueService.query.isPending;
  useEffect(() => {
    if (!!isAlerting == !!currentAlertId || isLoadingQueues) {
      return;
    }

    if (isAlerting) {
      const alertId = Date.now();
      setCurrentAlertId(alertId);
      getNextClickedStats.count = 0;
      getNextClickedStats.firstTimestamp = 0;
      /*
      disabling for now, because this is not used
      telemetrySend({
        name: 'QueueAlertResponseTime',
        key: '' + alertId,
        value: 0,
        properties: {
          startUrl: window.location.pathname,
        },
      });
      */
    } else {
      /*
      disabling for now, because this is not used
      telemetrySend({
        name: 'QueueAlertResponseTime',
        key: '' + currentAlertId,
        value: (Date.now() - currentAlertId!) / 1000,
        properties: {
          getNextClickCount: getNextClickedStats.count,
          getNextClickDelaySeconds: getNextClickedStats.firstTimestamp
            ? (getNextClickedStats.firstTimestamp - currentAlertId!) / 1000
            : null,
          endUrl: window.location.pathname,
          endCurrentlyLabeling: mealQueueService.currentlyLabellingOwnQueue,
          endOwnCount: mealQueueService.ownedCount,
          endAvailableCount: mealQueueService.availableCount,
        },
      });
      */
      setCurrentAlertId(null);
    }
  }, [isAlerting, currentAlertId, isLoadingQueues]);

  // Send a browser notification each time a new queue arives
  useEffect(() => {
    if (!isAlerting) {
      return;
    }

    // Determine whether a new queue has arrived
    // The newest queue by created_time; the first one wins a tie.
    const nextActionableQueue = mealQueueService.allActiveQueues
      .filter(q => !q.first_reviewer_user_id || q.first_reviewer_user_id == authInfo?.reviewer_id)
      .reduce<(typeof mealQueueService.allActiveQueues)[number] | undefined>(
        (newest, q) => (newest === undefined || q.created_time > newest.created_time ? q : newest),
        undefined,
      );
    if (!nextActionableQueue) {
      return;
    }

    if (flags.notification_UHP_only && !nextActionableQueue.is_ultra_priority_patient) {
      return;
    }

    if (!lastNotificationTimestamp) {
      lastNotificationTimestamp = nextActionableQueue.created_time;
    }

    if (nextActionableQueue.created_time <= lastNotificationTimestamp) {
      return;
    }

    // Send a browser notification
    lastNotificationTimestamp = nextActionableQueue.created_time;
    notify('New MPQ: ' + nextActionableQueue.id, {
      onClick: () => {
        if (!authInfo) {
          return;
        }
        getNextQueue({
          source: 'notification',
          medium: 'browser-native',
        });
      },
    });
  }, [isAlerting, mealQueueService.allActiveQueues, authInfo, notify, getNextQueue]);
  return null;
};

export const MealQueueNotificationService = () => {
  const { hasAuth } = useAuth();
  const { queue_polling: queuePolling } = useFeatures();
  const notificationsEnabled = hasAuth(Capabilities.mpqItemLabel) && queuePolling;
  return notificationsEnabled ? (
    <CombinedQueueProvider>
      <MealQueueNotificationServiceInner />
    </CombinedQueueProvider>
  ) : null;
};
